import { Injectable } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import {
  confirmSignUp,
  signIn,
  signUp,
  ConfirmSignUpInput,
  SignInOutput,
  SignUpInput,
  SignUpOutput,
} from "aws-amplify/auth";
import { AuthLocaleService } from "./auth-locale.service";

/** Where the address of a sign-up that waits for its code is kept. */
const PENDING_SIGN_UP_KEY = 'pending-sign-up';
/** A pending sign-up older than this is not offered again. */
const PENDING_SIGN_UP_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Cognito's answer to a wrong password. "User is disabled." comes with the same name. */
const WRONG_PASSWORD_MESSAGE = 'Incorrect username or password.';

interface PendingSignUp {
  email: string;
  createdAt: number;
}

/**
 * The native sign-up, from the form to the confirmed account.
 *
 * The authenticator keeps its step in memory only. After a reload or the back button it
 * starts on the sign-in screen, and the customer who could not find the code email has no
 * way back to "Resend code". A second sign-up with the same address used to end on
 * "account already exists". Two things answer that:
 *
 *  - The address of a sign-up that waits for its code stays in localStorage, so the login
 *    page can open on "Create account" with the address filled in.
 *  - A second sign-up of an unconfirmed account sends a new code and opens the code screen.
 */
@Injectable({
  providedIn: 'root'
})
export class SignUpService {
  constructor(private authLocale: AuthLocaleService,
              private translate: TranslateService) {
  }

  async signUp(input: SignUpInput): Promise<SignUpOutput> {
    const localized = this.authLocale.signUpInput(input);

    if (this.pendingEmail !== localized.username) {
      this.forgetPending();
    }

    try {
      return this.rememberIfPending(localized.username, await signUp(localized));
    } catch (error) {
      if (errorName(error) !== 'UsernameExistsException') {
        throw error;
      }

      return this.rememberIfPending(localized.username, await this.resume(localized, error));
    }
  }

  async confirmSignUp(input: ConfirmSignUpInput) {
    const output = await confirmSignUp(input);
    this.forgetPending();
    return output;
  }

  /** The address of a sign-up that still waits for its code, or null. */
  get pendingEmail(): string | null {
    const pending = this.readPending();

    if (!pending) {
      return null;
    }

    if (Date.now() - pending.createdAt > PENDING_SIGN_UP_MAX_AGE_MS) {
      this.forgetPending();
      return null;
    }

    return pending.email;
  }

  forgetPending(): void {
    try {
      localStorage.removeItem(PENDING_SIGN_UP_KEY);
    } catch {
      // Storage that cannot be written holds nothing to forget.
    }
  }

  /**
   * Picks up a sign-up whose account exists already.
   *
   * Cognito keeps the password of the first sign-up, so the password typed now is tried
   * first. The sign-in tells the cases apart:
   *
   *  - not confirmed: a new code goes out, and the authenticator shows the code screen.
   *  - wrong password: the customer is told to use the first password.
   *  - signed in: the account was confirmed and the password is right, so the customer is
   *    let in. The authenticator cannot leave its sign-up actor for "authenticated", so the
   *    page reloads and finds the session at its start.
   *
   * Anything else keeps the original "account already exists" error.
   */
  private async resume(input: SignUpInput, existsError: unknown): Promise<SignUpOutput> {
    let result: SignInOutput;

    try {
      result = await signIn({ username: input.username, password: input.password });
    } catch (error) {
      if (errorName(error) === 'NotAuthorizedException' && errorMessage(error) === WRONG_PASSWORD_MESSAGE) {
        throw new Error(this.translate.instant('auth.sign-up.errors.account-exists'));
      }

      throw existsError;
    }

    if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
      const codeDeliveryDetails = await this.authLocale.resendSignUpCode(input.username);

      return {
        isSignUpComplete: false,
        nextStep: { signUpStep: 'CONFIRM_SIGN_UP', codeDeliveryDetails },
      };
    }

    if (result.isSignedIn) {
      this.forgetPending();
      window.location.reload();
      // Keeps the form in its pending state until the reload takes the page away.
      return new Promise<never>(() => undefined);
    }

    throw existsError;
  }

  private rememberIfPending(email: string, output: SignUpOutput): SignUpOutput {
    if (output.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
      this.writePending({ email, createdAt: Date.now() });
    }

    return output;
  }

  private readPending(): PendingSignUp | null {
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_SIGN_UP_KEY) ?? 'null');

      if (typeof pending?.email === 'string' && typeof pending?.createdAt === 'number') {
        return pending;
      }
    } catch {
      // A value we cannot read is treated as no value.
    }

    return null;
  }

  private writePending(pending: PendingSignUp): void {
    try {
      localStorage.setItem(PENDING_SIGN_UP_KEY, JSON.stringify(pending));
    } catch {
      // Without storage the customer types the address again - nothing else is lost.
    }
  }
}

function errorName(error: unknown): string | undefined {
  return (error as { name?: string } | null)?.name;
}

function errorMessage(error: unknown): string | undefined {
  return (error as { message?: string } | null)?.message;
}
